import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Image, Button, Checkbox } from 'semantic-ui-react';
import './CreateAd.scss';
import PropTypes from 'prop-types';
import addAdvertisement from 'redux/actions/communication/advertisement/addAdvertisement';
import getSingleAdvertisement from 'redux/actions/communication/advertisement/getSingleAdvertisement';
import updateAdvertisement from 'redux/actions/communication/advertisement/updateAdvertisement';
import notify from 'helpers/notificationHelper';
import { WARN } from 'constants/notificationStatus';
import MyDropzone from 'components/common/DragAndDrop';
import PageLoader from 'components/common/PageLoader';
import isURL from 'helpers/isURL';
import { compactObject } from 'helpers/removeEmptyProperties';

const options = [
  { key: 'tap_and_win', text: 'Tap & Win', value: 'tap_and_win' },
  {
    key: 'external_link',
    text: 'External link',
    value: 'external_link',
  },
];

/**
 * Advertisement images grouped by color mode and language. Each field maps a
 * local state `key` to the API `field` name and a human readable `label`.
 */
const IMAGE_SECTIONS = [
  {
    mode: 'Light mode',
    languages: [
      {
        label: 'English',
        fields: [
          {
            key: 'lightLarge',
            field: 'light_large_image',
            label: 'Large Image',
          },
          {
            key: 'lightSmall',
            field: 'light_small_image',
            label: 'Small Image',
          },
        ],
      },
      {
        label: 'Kinyarwanda',
        fields: [
          {
            key: 'rwLightLarge',
            field: 'rw_light_large_image',
            label: 'Large Image',
          },
          {
            key: 'rwLightSmall',
            field: 'rw_light_small_image',
            label: 'Small Image',
          },
        ],
      },
    ],
  },
  {
    mode: 'Dark mode',
    languages: [
      {
        label: 'English',
        fields: [
          {
            key: 'darkLarge',
            field: 'dark_large_image',
            label: 'Large Image',
          },
          {
            key: 'darkSmall',
            field: 'dark_small_image',
            label: 'Small Image',
          },
        ],
      },
      {
        label: 'Kinyarwanda',
        fields: [
          {
            key: 'rwDarkLarge',
            field: 'rw_dark_large_image',
            label: 'Large Image',
          },
          {
            key: 'rwDarkSmall',
            field: 'rw_dark_small_image',
            label: 'Small Image',
          },
        ],
      },
    ],
  },
];

const IMAGE_FIELDS = IMAGE_SECTIONS.flatMap(section =>
  section.languages.flatMap(language =>
    language.fields.map(field => ({
      ...field,
      label: `${language.label} ${field.label} (${section.mode})`,
    })),
  ),
);

const emptyFiles = IMAGE_FIELDS.reduce(
  (acc, { key }) => ({ ...acc, [key]: null }),
  {},
);

const CreateAd = ({ editingAd, setEditingAd }) => {
  const [files, setFiles] = useState(emptyFiles);
  const [fileUrls, setFileUrls] = useState(emptyFiles);

  const [form, setForm] = useState({});

  const fileInputRefs = {
    lightSmall: useRef(null),
    lightLarge: useRef(null),
    darkSmall: useRef(null),
    darkLarge: useRef(null),
    rwLightSmall: useRef(null),
    rwLightLarge: useRef(null),
    rwDarkSmall: useRef(null),
    rwDarkLarge: useRef(null),
  };

  const dispatch = useDispatch();
  const [header, setHeader] = useState('New Advertisement');

  const { loading } = useSelector(
    ({ advertisement: { addAdvertisement } }) => addAdvertisement,
  );
  const { loading: updateLoading } = useSelector(
    ({ advertisement: { updateAdvertisement } }) =>
      updateAdvertisement,
  );
  const { data: singleAd, loading: singleAdLoading } = useSelector(
    ({ advertisement: { singleAdvertisement } }) =>
      singleAdvertisement,
  );

  const handleChange = ({ target: { name, value } }) => {
    setForm({ ...form, [name]: value });
  };

  const handleClear = () => {
    setForm({ title: '', has_action: false });

    setFiles(prevFiles => ({ ...prevFiles, ...emptyFiles }));
    setFileUrls(prevFiles => ({ ...prevFiles, ...emptyFiles }));
    setEditingAd(null);
  };

  /**
   * @param {string} key one of the IMAGE_FIELDS keys (e.g. lightSmall)
   */
  const clickFileAttchement = key => {
    fileInputRefs[key].current.click();
  };

  const validateImageType = file => {
    if (!file) return false;

    const validTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif',
    ];
    const validExtensions = ['.png', '.jpg', '.jpeg', '.gif'];

    const fileName = file.name.toLowerCase();
    const hasValidType = validTypes.includes(file.type);
    const hasValidExtension = validExtensions.some(ext =>
      fileName.endsWith(ext),
    );

    return hasValidType || hasValidExtension;
  };

  const handleChangeAttach = e => {
    e.preventDefault();

    const name = e.target?.name;
    const file = e.target?.files[0];

    if (!validateImageType(file)) {
      notify({
        message:
          'Please upload a valid image file (PNG, JPG, JPEG, or GIF)',
        status: WARN,
      });
      e.target.value = null;
      return;
    }

    const objectUrl = URL.createObjectURL(e.target?.files[0]);

    setFiles(prevFiles => {
      return {
        ...prevFiles,
        [name]: file,
      };
    });

    setFileUrls(prevUrls => {
      return {
        ...prevUrls,
        [name]: objectUrl,
      };
    });

    return () => URL.revokeObjectURL(objectUrl);
  };

  const handleUpdate = () => {
    const formData = new FormData();

    const invalidType = IMAGE_FIELDS.find(
      ({ key }) => files[key] && !validateImageType(files[key]),
    );
    if (invalidType) {
      return notify({
        message: `${invalidType.label} file must be a valid image (PNG, JPG, JPEG, or GIF)`,
        status: WARN,
      });
    }

    const tooLarge = IMAGE_FIELDS.find(
      ({ key }) => files[key] && files[key].size / 1024 / 1024 > 3,
    );
    if (tooLarge) {
      return notify({
        message: `${tooLarge.label} file should not exceed 3 MBs`,
        status: WARN,
      });
    }

    IMAGE_FIELDS.forEach(({ key, field }) => {
      if (files[key]) {
        formData.append(field, files[key]);
      }
    });

    if (!form.has_action) {
      form.action_content = undefined;
      form.action_label = undefined;
      form.action_type = undefined;
    }

    if (form.action_type === 'tap_and_win') {
      form.action_content = undefined;
    }

    const compactForm = compactObject(form);

    Object.keys(compactForm).forEach(key => {
      formData.append(key, compactForm[key]);
    });

    updateAdvertisement(editingAd, formData)(dispatch, () => {
      handleClear();
    });
  };

  const handleSubmit = () => {
    const formData = new FormData();

    // All English and Kinyarwanda images are required on creation.
    const missing = IMAGE_FIELDS.find(({ key }) => !files[key]);
    if (missing) {
      return notify({
        message: `${missing.label} is required`,
        status: WARN,
      });
    }

    const invalidType = IMAGE_FIELDS.find(
      ({ key }) => !validateImageType(files[key]),
    );
    if (invalidType) {
      return notify({
        message: `${invalidType.label} file must be a valid image (PNG, JPG, JPEG, or GIF)`,
        status: WARN,
      });
    }

    const tooLarge = IMAGE_FIELDS.find(
      ({ key }) => files[key].size / 1024 / 1024 > 3,
    );
    if (tooLarge) {
      return notify({
        message: `${tooLarge.label} file should not exceed 3 MBs`,
        status: WARN,
      });
    }

    if (form.status === 'active') {
      formData.append('status', form.status);
    }

    if (!form.has_action) {
      form.action_content = undefined;
      form.action_label = undefined;
      form.action_type = undefined;
    }

    if (form.action_type === 'tap_and_win') {
      form.action_content = undefined;
    }

    const compactForm = compactObject(form);

    Object.keys(compactForm).forEach(key => {
      if (key !== 'status') {
        formData.append(key, compactForm[key]);
      }
    });

    IMAGE_FIELDS.forEach(({ key, field }) => {
      formData.append(field, files[key]);
    });

    addAdvertisement(formData)(dispatch, () => {
      handleClear();
    });
  };

  useEffect(() => {
    if (editingAd) {
      getSingleAdvertisement(editingAd)(dispatch);

      setHeader('Editing Advertisement');
    }
    setForm({});
  }, [editingAd]);

  const isButtonDisabled = () => {
    if (editingAd) {
      return (
        !form?.title ||
        form?.title === '' ||
        updateLoading ||
        (form.has_action === true &&
          (!form.action_type ||
            form.action_type === '' ||
            !form.action_label ||
            (form.action_type === 'external_link' &&
              (!form.action_content || !isURL(form.action_content)))))
      );
    }
    return (
      IMAGE_FIELDS.some(({ key }) => !files[key]) ||
      !form?.title ||
      form?.title === '' ||
      loading ||
      (form.has_action === true &&
        (!form.action_type ||
          form.action_type === '' ||
          !form.action_label ||
          (form.action_type === 'external_link' &&
            (!form.action_content || !isURL(form.action_content)))))
    );
  };

  useEffect(() => {
    if (singleAd) {
      setForm({
        ...form,
        action_content: singleAd.action_content,
        action_label: singleAd.action_label,
        action_type: singleAd.action_type,
        has_action: singleAd.has_action,
        title: singleAd.title,
        status: singleAd.status,
      });
    }
  }, [singleAd]);

  return (
    <div className="create-new-ad">
      <>
        {!!form?.title || files.lightSmall ? (
          <div className="header">
            <h3>{header}</h3>
            <Button
              content="Clear"
              className="header__clear"
              onClick={() => {
                handleClear();
                setHeader('New Advertisement');
              }}
            />
          </div>
        ) : (
          <h3>{header}</h3>
        )}
        {editingAd ? (
          singleAdLoading ? (
            <PageLoader />
          ) : (
            <Form className="create-ad-form">
              {IMAGE_SECTIONS.map(section => (
                <div className="form-dropzones" key={section.mode}>
                  <div className="form-dropzones__title">
                    {section.mode}
                  </div>
                  {section.languages.map(language => (
                    <div
                      className="form-dropzones__language"
                      key={language.label}
                    >
                      <div className="form-dropzones__language-title">
                        {language.label}
                      </div>
                      <div className="form-dropzones__inner">
                        {language.fields.map(field => (
                          <div key={field.key}>
                            <span>{field.label}</span>
                            <div className="editing_image">
                              <Image
                                file
                                name={field.key}
                                src={
                                  files[field.key]
                                    ? fileUrls[field.key]
                                    : singleAd?.[field.field]
                                }
                                alt=""
                                className="editing_imaged__file"
                              />
                              <input
                                className="attachment"
                                ref={fileInputRefs[field.key]}
                                type="file"
                                name={field.key}
                                required
                                accept="image/*"
                                onChange={handleChangeAttach}
                              />

                              <button
                                className="editing_image__button"
                                type="button"
                                onClick={() =>
                                  clickFileAttchement(field.key)
                                }
                              >
                                Change Image
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              <div className="form-inputs">
                <Form.Input
                  onChange={handleChange}
                  required
                  className="input_title"
                  defaultValue={singleAd?.title}
                  label="Subject"
                  name="title"
                  placeholder="Subject"
                />

                <div className="action-group">
                  <span>Does this add has action ?</span>
                  <div className="action-group__checkbox">
                    <Checkbox
                      toggle
                      className="publish-checkbox"
                      checked={form.has_action}
                      onChange={(_, { checked }) =>
                        setForm({
                          ...form,
                          has_action: checked,
                        })
                      }
                    />
                  </div>
                </div>

                {form.has_action && (
                  <>
                    <Form.Select
                      fluid
                      required
                      onChange={(_, { value }) => {
                        setForm({
                          ...form,
                          action_type: value,
                        });
                      }}
                      defaultValue={singleAd?.action_type}
                      options={options}
                      label="Action type"
                      name="action_type"
                      placeholder="Action type"
                    />
                    {form.action_type === 'external_link' && (
                      <Form.Input
                        fluid
                        required
                        onChange={handleChange}
                        defaultValue={singleAd?.action_content}
                        label="Link"
                        name="action_content"
                        placeholder="External link"
                      />
                    )}
                    <Form.Input
                      fluid
                      required
                      onChange={handleChange}
                      defaultValue={singleAd?.action_label}
                      label="Button label"
                      name="action_label"
                      placeholder="Button label"
                    />
                  </>
                )}

                <div className="send-btn-wrapper">
                  <Form.Button
                    primary
                    className="create"
                    disabled={isButtonDisabled()}
                    loading={updateLoading}
                    onClick={handleUpdate}
                  >
                    Save
                  </Form.Button>
                </div>
              </div>
            </Form>
          )
        ) : (
          <Form onSubmit={handleSubmit} className="create-ad-form">
            {IMAGE_SECTIONS.map(section => (
              <div className="form-dropzones" key={section.mode}>
                <div className="form-dropzones__title">
                  {section.mode}
                </div>
                {section.languages.map(language => (
                  <div
                    className="form-dropzones__language"
                    key={language.label}
                  >
                    <div className="form-dropzones__language-title">
                      {language.label}
                    </div>
                    <div className="form-dropzones__inner">
                      {language.fields.map(field => (
                        <MyDropzone
                          key={field.key}
                          label={field.label}
                          setFile={file =>
                            setFiles(prev => ({
                              ...prev,
                              [field.key]: file,
                            }))
                          }
                          file={files[field.key]}
                          name={field.key}
                          handleChangeAttach={handleChangeAttach}
                          clickFileAttchement={() =>
                            clickFileAttchement(field.key)
                          }
                          fileInputRef={fileInputRefs[field.key]}
                          accept="image/png,image/jpeg,image/gif"
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <div className="form-inputs">
              <Form.Input
                onChange={handleChange}
                required
                value={form.title}
                label="Subject"
                name="title"
                placeholder="Subject"
              />
              <div className="action-group">
                <span>Does this add has action ?</span>
                <div className="action-group__checkbox">
                  <Checkbox
                    toggle
                    className="publish-checkbox"
                    checked={form.has_action}
                    onChange={(_, { checked }) =>
                      setForm({
                        ...form,
                        has_action: checked,
                      })
                    }
                  />
                </div>
              </div>

              {form.has_action && (
                <>
                  <Form.Select
                    fluid
                    required
                    onChange={(_, { value }) => {
                      setForm({
                        ...form,
                        action_type: value,
                      });
                    }}
                    value={form.action_type}
                    options={options}
                    label="Action type"
                    name="action_type"
                    placeholder="Action type"
                  />
                  {form.action_type === 'external_link' && (
                    <Form.Input
                      fluid
                      required
                      onChange={handleChange}
                      value={form.link}
                      label="Link"
                      name="action_content"
                      placeholder="External link"
                    />
                  )}
                  <Form.Input
                    fluid
                    required
                    onChange={handleChange}
                    value={form.action_label}
                    label="Button label"
                    name="action_label"
                    placeholder="Button label"
                  />
                </>
              )}

              <div className="send-btn-wrapper">
                <Checkbox
                  toggle
                  label="publish"
                  className="publish-checkbox"
                  checked={form.status === 'active'}
                  onChange={(_, { checked }) =>
                    setForm({
                      ...form,
                      status: checked ? 'active' : 'inactive',
                    })
                  }
                />
                <Form.Button
                  primary
                  loading={loading}
                  type="submit"
                  className="create"
                  disabled={isButtonDisabled()}
                >
                  Create Ad
                </Form.Button>
              </div>
            </div>
          </Form>
        )}
      </>
    </div>
  );
};

CreateAd.defaultProps = {};

CreateAd.propTypes = {
  editingAd: PropTypes.number.isRequired,
  setEditingAd: PropTypes.func.isRequired,
};
export default CreateAd;
